import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Coffee, Film, Users, Sparkles, TreePine, Wrench, Handshake } from "lucide-react";
import { api } from "../lib/api";
import EventCard from "../components/EventCard";
import InterestForm from "../components/InterestForm";
import useSeo from "../hooks/useSeo";

const PILLARS = [
  { icon: Coffee, title: "Coffee & long conversations", text: "Small tasting circles. Phones in a basket. Whoever brings the snacks decides the playlist." },
  { icon: Film, title: "Watch parties, on a wall", text: "Old films, new films, weird films. We stay for the conversation after the credits." },
  { icon: TreePine, title: "Nature walks", text: "Slow morning walks through old gardens, lakes, and city forests. Pace set by the slowest one in the group." },
  { icon: Users, title: "Senior getaways", text: "Easy mornings designed for seniors — gentle walks, chai, story circles, benches every few minutes." },
  { icon: Wrench, title: "Lifeskill building", text: "Small workshops on things schools forgot — fixing a flat tyre, cleaning your own AC, sorting out your personal finances." },
];

export default function Home() {
  const [events, setEvents] = useState([]);
  useSeo({
    title: "saamne — in-person experiences across Hyderabad, Bangalore & Pune",
    description:
      "saamne hosts in-person experiences (only) across Hyderabad, Bangalore and Pune — coffee tastings, watch parties, nature walks, senior getaways, and lifeskill workshops. RSVP or propose an event.",
    path: "/",
  });

  useEffect(() => {
    api.get("/events").then((r) => setEvents(r.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="max-w-6xl mx-auto px-6 md:px-10 pt-16 md:pt-24 pb-16 md:pb-24">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-end">
            <div className="lg:col-span-7">
              <div className="label-eyebrow mb-6" data-testid="hero-eyebrow">
                Hyderabad · Bangalore · Pune · small gatherings · real people · since 2026
              </div>
              <h1 className="hero-fade font-serif text-5xl sm:text-6xl lg:text-7xl leading-[1.02] tracking-tight"
                  style={{ color: "var(--text-main)" }}>
                Come over. <br />
                <span style={{ color: "var(--highlight)" }}>Stay a while.</span>
              </h1>
              <p className="mt-7 max-w-xl text-[17px] md:text-lg leading-relaxed" style={{ color: "var(--text-muted)" }}>
                <span className="font-serif italic text-xl" style={{ color: "var(--highlight)" }}>saamne</span> — Hindi/Urdu for <em style={{ color: "var(--highlight)" }}>face to face</em>. We bring small groups of real people together for the kind of evenings the internet keeps trying to replace — a slow coffee tasting, a sunrise nature walk, a hands-on workshop, a supper you didn't have to cook. No big crowds. No passive audiences. Just people, present, in the same room.
              </p>
              <div className="mt-9 flex flex-wrap items-center gap-3 hero-cta-group">
                <a href="#rsvp" data-testid="hero-cta-rsvp" className="btn-primary">
                  I'm Interested <ArrowRight size={16} />
                </a>
                <Link to="/propose" data-testid="hero-cta-propose" className="btn-secondary">
                  Suggest a Gathering <ArrowUpRight size={16} />
                </Link>
                <Link to="/copartner" data-testid="hero-cta-copartner" className="btn-secondary">
                  Partner with us <ArrowUpRight size={16} />
                </Link>
                <Link to="/join" data-testid="hero-cta-join" className="btn-secondary">
                  Host with us <ArrowUpRight size={16} />
                </Link>
              </div>
            </div>
            <div className="lg:col-span-5 relative">
              <div className="card-warm relative overflow-hidden">
                <img
                  src="https://images.unsplash.com/photo-1529156069898-49953e39b3ac?w=1200&q=80"
                  alt="A multi-generational gathering of people across ages sharing a meal and laughter together"
                  className="w-full aspect-[4/5] object-cover"
                />
                <div
                  aria-hidden
                  className="absolute inset-0 pointer-events-none"
                  style={{ background: "rgba(255, 235, 210, 0.12)" }}
                />
              </div>
              <div
                className="mt-6 note-card"
                data-testid="hero-floating-card"
              >
                <div className="label-eyebrow">A note</div>
                <p className="font-serif text-xl mt-2 leading-snug">
                  "Tell us what you'd love to be invited to. We'll host the most-loved ones — and personally reach out when it's ready."
                </p>
                <p className="text-xs mt-3" style={{ color: "var(--fine-muted)" }}>
                  — <a href="#rsvp" className="underline" style={{ color: "var(--highlight)" }}>I'm interested</a>, or <Link to="/propose" className="underline" style={{ color: "var(--highlight)" }}>suggest a gathering</Link>
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <hr className="hr-warm max-w-6xl mx-auto" data-testid="hero-rsvp-divider" />

      {/* RSVP / Interested form — primary funnel */}
      <section id="rsvp" className="max-w-6xl mx-auto px-6 md:px-10 pt-12 pb-20">
        <div className="grid lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-5">
            <div className="label-eyebrow">Get invited</div>
            <h2 className="font-serif text-4xl md:text-5xl mt-4 leading-tight">
              Which one <em style={{ color: "var(--highlight)" }}>sounds like</em> your kind of thing?
            </h2>
            <p className="mt-5 text-[17px] md:text-lg leading-relaxed" style={{ color: "var(--text-muted)" }}>
              Pick what calls to you and tell us your city. Leave your name and number — we'll reach out personally the next time we host something near you.
            </p>
            <p className="fine-note mt-4">
              No newsletters. No algorithm. Just a real person, and a real invite.
            </p>
          </div>
          <div className="lg:col-span-7">
            <InterestForm compact />
          </div>
        </div>
      </section>

      {/* WHAT WE DO */}
      <section className="max-w-6xl mx-auto px-6 md:px-10 pt-6 pb-20">
        <div className="grid md:grid-cols-12 gap-8 items-start">
          <div className="md:col-span-4">
            <div className="label-eyebrow">What we do</div>
            <h2 className="font-serif text-4xl md:text-5xl mt-4 leading-tight">
              Small rooms, <br /> authentic evenings, <br /> real faces.
            </h2>
          </div>
          <div className="md:col-span-8 grid sm:grid-cols-2 gap-5">
            {PILLARS.map((p, i) => (
              <div
                key={p.title}
                data-testid={`pillar-${i}`}
                className="card-warm p-7 fade-up"
                style={{ animationDelay: `${i * 80}ms` }}
              >
                <p.icon size={26} style={{ color: "var(--primary)" }} />
                <h3 className="font-serif text-2xl mt-4">{p.title}</h3>
                <p className="text-sm mt-3 leading-relaxed" style={{ color: "var(--text-muted)" }}>
                  {p.text}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* COMING UP — masterclass-in-MVP placeholder */}
      <section className="max-w-6xl mx-auto px-6 md:px-10 pb-20">
        <div className="flex items-end justify-between mb-10">
          <div>
            <div className="label-eyebrow">Coming up · Hyderabad · Bangalore · Pune</div>
            <h2 className="font-serif text-4xl md:text-5xl mt-3">Getting ready for you.</h2>
          </div>
        </div>
        <div className="card-warm p-10 md:p-16 text-center relative overflow-hidden paper-noise" data-testid="coming-soon">
          <div className="relative">
            <div className="text-5xl md:text-6xl mb-4 font-serif">🫖 ☕ 🎬 🌿 🔧</div>
            <p className="font-serif text-3xl md:text-4xl leading-tight max-w-3xl mx-auto">
              We're brewing, dusting the projector, watering the plants and oiling the wrench.
            </p>
            <p className="mt-5 max-w-2xl mx-auto" style={{ color: "var(--text-muted)" }}>
              First gathering drops the moment we've got fifteen of you saying <em>yes, I'd love that</em>. The fastest way to make it happen?
            </p>
            <div className="mt-7 flex flex-wrap items-center justify-center gap-3">
              <a href="#rsvp" className="btn-primary" data-testid="coming-soon-rsvp">I'm Interested <ArrowRight size={16} /></a>
              <Link to="/propose" className="btn-secondary" data-testid="coming-soon-propose">Suggest a Gathering</Link>
            </div>
            <p className="mt-6 text-xs" style={{ color: "var(--text-muted)" }}>
              ( PS: We're keeping this slow on purpose. A masterclass in patience, not in pop-ups. )
            </p>
          </div>
        </div>
      </section>

      {/* THREE PATHS */}
      <section className="max-w-6xl mx-auto px-6 md:px-10 pb-24">
        <div className="grid md:grid-cols-3 gap-6">
          <Link to="/propose" data-testid="home-card-propose" className="card-warm p-8 group block">
            <div className="label-eyebrow">For the next six months</div>
            <h2 className="font-serif text-2xl md:text-3xl mt-3 leading-tight group-hover:text-[color:var(--primary)] transition-colors">
              Propose an event.
            </h2>
            <p className="mt-4 text-sm" style={{ color: "var(--text-muted)" }}>
              Tell us what you'd love to see hosted in Hyderabad, Bangalore or Pune. We read every proposal personally.
            </p>
            <span className="inline-flex items-center gap-2 mt-5 text-sm" style={{ color: "var(--primary)" }}>
              Send a proposal <ArrowUpRight size={16} />
            </span>
          </Link>
          <Link to="/copartner" data-testid="home-card-copartner" className="card-warm p-8 group block">
            <div className="label-eyebrow">Host with us</div>
            <h2 className="font-serif text-2xl md:text-3xl mt-3 leading-tight group-hover:text-[color:var(--primary)] transition-colors">
              Co-partner an event.
            </h2>
            <p className="mt-4 text-sm" style={{ color: "var(--text-muted)" }}>
              Have a rooftop, a skill, a curated reel, a Sunday morning? Bring what you have — we'll bring the crowd, the invite list and the crew.
            </p>
            <span className="inline-flex items-center gap-2 mt-5 text-sm" style={{ color: "var(--primary)" }}>
              Offer to co-host <Handshake size={16} />
            </span>
          </Link>
          <Link to="/join" data-testid="home-card-join" className="card-warm p-8 group block">
            <div className="label-eyebrow">Gig with us</div>
            <h2 className="font-serif text-2xl md:text-3xl mt-3 leading-tight group-hover:text-[color:var(--primary)] transition-colors">
              Send your interest.
            </h2>
            <p className="mt-4 text-sm" style={{ color: "var(--text-muted)" }}>
              Paid gigs and volunteering, for people who care about the kind of in-person world we're trying to build.
            </p>
            <span className="inline-flex items-center gap-2 mt-5 text-sm" style={{ color: "var(--primary)" }}>
              Join the crew <ArrowUpRight size={16} />
            </span>
          </Link>
        </div>
      </section>
    </div>
  );
}
